import React from "react";
import { RouteComponentProps } from "react-router";
import {
    IDatasource,
    GetRowIdParams,
    ModuleRegistry,
    InfiniteRowModelModule,
    provideGlobalGridOptions,
    ValidationModule,
    SideBarModule,
    CustomEditorModule,
    TextEditorModule,
    CellStyleModule,
    TooltipModule,
    LargeTextEditorModule,
    HighlightChangesModule,
    ColumnApiModule,
    ScrollApiModule,
    ColumnsToolPanelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    MultiFilterModule,
    SetFilterModule,
    GroupFilterModule,
    CustomFilterModule,
    QuickFilterModule,
    ExternalFilterModule,
    AdvancedFilterModule,
    RenderApiModule,
    ExcelExportModule,
    CellSelectionModule,
    ClipboardModule,
    ColumnMenuModule,
    ContextMenuModule, RowSelectionModule, RowApiModule, ColumnAutoSizeModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {action, observable, reaction, runInAction} from "mobx";
import { observer } from "mobx-react";
import { autoinject, bind, FormStore, inject} from "Mri.Web.Common";
import {AccountStore, NO_ACTIVE_PRESET} from "../../Account/AccountStore";
import { AppStore } from "../../Core/AppStore";
import { DisplayListViewModel } from "../../DisplayFilters/DisplayListViewModel";
import { ActiveDisplay } from "../ActiveDisplay";
import { DisplaysStore } from "../DisplaysStore";
import {isEqual} from "lodash";
import {PinnedColumnManager} from "./GridHandlers/PinnedColumnManager";
import {GridScrollManager} from "./GridHandlers/GridScrollManager";
import {ColumnStateManager} from "./GridHandlers/ColumnStateManager";
import {GridEventManager} from "./GridHandlers/GridEventManager";
import {GridConfig} from "./GridConfig";

// Mark all grids as using legacy themes
provideGlobalGridOptions({
    theme: "legacy",
});

ModuleRegistry.registerModules([ 
    InfiniteRowModelModule, 
    ValidationModule, 
    SideBarModule, 
    CustomEditorModule, 
    TextEditorModule, 
    CellStyleModule, 
    TooltipModule,
    LargeTextEditorModule,
    HighlightChangesModule,
    ColumnApiModule,
    ScrollApiModule,
    ColumnsToolPanelModule,
    TextFilterModule, 
    NumberFilterModule, 
    DateFilterModule, 
    SetFilterModule, 
    MultiFilterModule, 
    GroupFilterModule, 
    CustomFilterModule, 
    QuickFilterModule, 
    ExternalFilterModule, 
    AdvancedFilterModule,
    RenderApiModule,
    CellSelectionModule,
    ClipboardModule,
    ColumnMenuModule,
    ContextMenuModule,
    ExcelExportModule,
    RowSelectionModule,
    RowApiModule,
    ColumnAutoSizeModule
]);

export interface IDisplayGridProps extends RouteComponentProps<undefined>{
    readonly vm: DisplayListViewModel;
}

@autoinject
@observer
export class DisplayGrid extends React.Component<IDisplayGridProps> {
    @action
    public async componentDidMount() {
        this.initializeGridKey();
        this.syncActiveColumnPreset();
        this.gridScrollManager.initScrollListeners();
        this.setupResizeListener();
        await this.loadInitialData();
        this.setupRefreshTimer();
        this.setupColumnReactions();
    }


    public componentWillUnmount() {
        if (this.refreshTimer) window.clearInterval(this.refreshTimer);
        this.gridScrollManager.removeScrollListeners();

        this.appStore.gridInitialized = false;
    }

    public render() {
        const { busy, loading } = this.props.vm;
        return (
            <div className={this.gridConfig.gridTheme} style={{width: '100%', height: '100%'}}>
                <AgGridReact
                    key={this.appStore.gridKey}
                    ref={r => this.bindGridRef(r)}
                    // GRID OPTIONS AND SETTINGS
                    gridOptions={this.gridConfig.gridOptions}
                    getRowId={this.getRowId}
                    columnDefs={this.gridConfig.colDefs}
                    defaultColDef={this.gridConfig.defaultColDef}
                    infiniteInitialRowCount={1000}
                    cacheBlockSize={this.props.vm.limit}
                    cacheOverflowSize={10}
                    rowBuffer={3}
                    rowModelType={'infinite'}
                    maxConcurrentDatasourceRequests={1}
                    rowHeight={this.gridEventManager.rowHeight}
                    loading={busy || loading}
                    // GRID EVENTS
                    onFirstDataRendered={this.initializeGrid}
                    onSortChanged={this.gridEventManager.onSortChange}
                    onCellValueChanged={this.gridEventManager.onCellChangedAsync}
                    onColumnResized={this.gridEventManager.onGridChange}
                    onColumnMoved={this.gridEventManager.onGridChange}
                    onColumnPinned={this.gridEventManager.onGridChange}
                    onColumnVisible={this.gridEventManager.onGridChange}
                    onToolPanelVisibleChanged={this.gridEventManager.onToolPanelToggled}
                    onGridReady={this.onGridReady}
                    onBodyScroll={this.gridScrollManager.onBodyScroll}
                />

                {/* Draggable resizers */}
                <div ref={this.ghostLineRef} className="resizer-ghost-line"/>
                {this.accountStore.storage.gridLeftPinnedContainerScrollable &&
                    <div
                        ref={this.leftResizerRef}
                        className="left-pinned-resizer"
                        onMouseDown={this.pinnedColumnManager.startLeftResize}
                        onTouchStart={this.pinnedColumnManager.startLeftResize}
                    />
                }
                {this.accountStore.storage.gridRightPinnedContainerScrollable &&
                    <div
                        ref={this.rightResizerRef}
                        className="right-pinned-resizer"
                        onMouseDown={this.pinnedColumnManager.startRightResize}
                        onTouchStart={this.pinnedColumnManager.startRightResize}
                    />
                }
            </div>
        );
    }

    @bind
    @action
    private async onGridReady() {
        if (this.appStore.gridRef) {
            const dataSource: IDatasource = {
                rowCount: undefined,
                getRows: async (params) => {
                    // At this point in your code, you would call the server.
                    while (this.props.vm.filterChangeInProgress) {
                        await new Promise(resolve => setTimeout(resolve, 50));
                    }

                    let displaysLength = this.props.vm.displays.length;
                    let total = this.props.vm.total;
                    
                    try {
                        if (displaysLength != params.endRow || displaysLength == 0) {
                            if (this.gridEventManager.sortChanging) {
                                this.gridEventManager.sortChanging = false
                                await this.props.vm.onDisplayFilterChange();
                            } else if (!this.props.vm.filterChangeInProgress && displaysLength < params.endRow && displaysLength != total) {
                                // Pull new data from server
                                // console.debug("asking for " + params.startRow + " to " + params.endRow + ' Displays: ' + displaysLength + '/' + total);
                                await this.loadMoreAsync();
                                await this.props.vm.loadOptionsAsync();
                                this.pinnedColumnManager.applyMaxPinnedColumnContainer();
                            } else {
                                // Skip server request and pulls refreshed data from the vm
                                // console.debug("refreshing for " + params.startRow + " to " + params.endRow + ' Displays: ' + displaysLength + '/' + total);
                            }
                        }
                    } catch (e) {
                        console.error(e)
                    } finally {
                        const rowsThisPage = this.props.vm.displays.slice(params.startRow, params.endRow);

                        // if on or after the last page, work out the last row.
                        this.appStore.gridRef?.api.setRowCount(this.props.vm.total);

                        let lastRow = -1;
                        if (this.props.vm.total <= params.endRow) {
                            lastRow = this.props.vm.total;
                        }
                        // call the success callback
                        params.successCallback(rowsThisPage, lastRow);
                    }
                },
            };

            this.appStore.gridRef?.api.setGridOption('datasource', dataSource);

            // This adds a close button to the column panel.
            setTimeout(() => {
                const header = document.querySelector('.ag-column-select-header');
                if (header && !header.querySelector('.custom-close-button')) {
                    const closeButton = document.createElement('button');
                    closeButton.innerText = '×';
                    closeButton.className = 'custom-close-button';
                    closeButton.style.cssText = `
                        margin-left: auto;
                        background: transparent;
                        border: none;
                        font-size: 16px;
                        cursor: pointer;
                    `;
                    
                    closeButton.onclick = () => {
                        this.appStore.gridRef?.api.closeToolPanel();
                    };
                    header.appendChild(closeButton);
                }
            }, 100); // Adjust delay if needed
            
           if(this.appStore.gridKey == 0) {
               this.appStore.gridAutoHeaderHeight = true;

               setTimeout(() => {
                   // Measure the header height
                   const headerHeight = document.querySelector('.ag-header-cell')?.clientHeight;

                   if (headerHeight) {
                       runInAction(() => {
                           this.appStore.gridHeaderHeight = headerHeight;
                           this.appStore.gridAutoHeaderHeight = false;
                            
                           this.appStore.gridInitialized = false;
                           this.appStore.gridKey++; // Triggers re-render
                       });
                   }
               }, 1000);
           }
        }
    }

    @bind
    @action
    private async initializeGrid() {
        if (this.appStore.gridRef != null) {
            this.columnStateManager.applyPresetColumns();

            this.appStore.gridInitialized = true;

            setTimeout(() => {
                this.pinnedColumnManager.applyMaxPinnedColumnContainer();
                
                this.gridScrollManager.restoreScrollPositions(2500);
            }, 1000);
        }
    }
    
    @bind
    @action
    private refreshGrid(){
        if(!this.appStore.isModalOpen) {
            this.appStore.gridRef?.api.refreshInfiniteCache();
            this.pinnedColumnManager.applyMaxPinnedColumnContainer();
        }
    }
   
    @bind
    @action
    private async loadMoreAsync() {
        await this.props.vm.loadMoreAsync();
    }

    // This tells the Grid where to put rows when data is refreshed.
    @bind
    @action
    private getRowId(params: GetRowIdParams<ActiveDisplay>) {
        return params.data.serialNumber;
    }

    @bind
    @action
    private bindGridRef(ref: AgGridReact<ActiveDisplay> | null) {
        this.appStore.gridRef = ref;
    }

    @bind
    @action
    private initializeGridKey() {
        this.appStore.gridKey = 0;
    }

    @bind
    @action
    private syncActiveColumnPreset() {
        if (
            !this.accountStore.tabStorage.activeGridColumnPreset &&
            this.accountStore.storage.activeGridColumnPreset !== NO_ACTIVE_PRESET
        ) {
            this.accountStore.tabStorage.activeGridColumnPreset =
                this.accountStore.storage.activeGridColumnPreset;
        }
    }

    @bind
    @action
    private setupResizeListener() {
        if (
            this.accountStore.storage.gridLeftPinnedContainerScrollable ||
            this.accountStore.storage.gridRightPinnedContainerScrollable
        ) {
            window.addEventListener("resize", () => {
                this.pinnedColumnManager.applyMaxPinnedColumnContainer();
            });
        }
    }

    @bind
    @action
    private async loadInitialData() {
        try {
            await this.props.vm.loadAccountsAsync();
            await this.props.vm.loadContractsAsync();
            await this.props.vm.loadAllOptionsAsync();
            await this.props.vm.loadOptionsAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        }
    }

    @bind
    @action
    private setupRefreshTimer() {
        this.refreshTimer = window.setInterval(this.refreshGrid, 10000);
    }

    @bind
    @action
    private setupColumnReactions() {
        reaction(
            () =>
                this.accountStore.storage.gridColumnPresets.map((preset) => ({
                    name: preset.name,
                    columns: preset.columns.map((col) => ({
                        colId: col.colId,
                        width: col.width,
                        hide: col.hide,
                        pinned: col.pinned,
                    })),
                })),
            () => {
                this.columnStateManager.applyPresetColumns();
            },
            { equals: isEqual }
        );

        reaction(
            () =>
                this.accountStore.storage.noActivePresetGridColumns.map((col) => ({
                    colId: col.colId,
                    width: col.width,
                    hide: col.hide,
                    pinned: col.pinned,
                })),
            () => {
                if (!this.accountStore.storage.activeColumnPreset) {
                    this.columnStateManager.applyPresetColumns();
                }
            },
            { equals: isEqual }
        );
    }


    @observable
    private leftResizerRef = React.createRef<HTMLDivElement>();
    @observable
    private rightResizerRef = React.createRef<HTMLDivElement>();
    @observable
    private ghostLineRef = React.createRef<HTMLDivElement>();
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private accountStore!: AccountStore;
    private refreshTimer?: number;
    @observable
    private gridConfig = new GridConfig(this.accountStore, this.appStore);
    @observable
    private pinnedColumnManager = new PinnedColumnManager(
        this.accountStore,
        this.appStore,
        this.ghostLineRef,
        this.leftResizerRef,
        this.rightResizerRef
    );
    @observable
    private gridScrollManager = new GridScrollManager(this.accountStore);
    @observable
    private columnStateManager = new ColumnStateManager(this.accountStore, this.appStore, this.props.vm);
    @observable
    private gridEventManager = new GridEventManager(
        this.accountStore,
        this.appStore,
        this.displaysStore,
        this.columnStateManager,
        this.pinnedColumnManager,
        this.gridScrollManager,
        this.props.vm
    );
}
